/**
 * @fileoverview Whether today is one of the first days this month that Today has been
 * opened, which is when the wettest or the driest month is said: the first
 * as many as the Grow settings say (today.RAIN_MONTH_OPENS; 0 never).
 *
 * Said every day it would be wallpaper by the end of the week; said once it
 * is easy to miss. Two opening days is the compromise the owner chose. Kept
 * in this browser only: a phone and a laptop each say it on their own
 * opening days, which is no harm, and nothing about it needs to reach the
 * server.
 *
 * Where: TodayPage.
 * API: none; localStorage under RAIN_MONTH_STORAGE_KEY.
 * Settings: Grow setting today.RAIN_MONTH_OPENS (0 never; not said before the app has heard it).
 *
 * @module useRainMonth
 */

import { useState } from 'react';
import { useGrowSettings } from '@/shared/lib/growSettings';
import { RAIN_MONTH_STORAGE_KEY } from '@/config/tunables/today';

/**
 * Notes today as a day the page was opened this month, and gives back every
 * such day so far, in order. Null when storage is refused (a private window).
 *
 * @param iso - The gardener's today, "YYYY-MM-DD".
 * @returns The month's opening days so far, today included.
 */
function openingDays(iso: string): string[] | null {
  try {
    const month = iso.slice(0, 7);
    const saved = JSON.parse(window.localStorage.getItem(RAIN_MONTH_STORAGE_KEY) ?? 'null') as { month?: string; days?: string[] } | null;
    const days = saved?.month === month && Array.isArray(saved.days) ? saved.days : [];
    if (days.includes(iso)) return days;
    const next = [...days, iso];
    window.localStorage.setItem(RAIN_MONTH_STORAGE_KEY, JSON.stringify({ month, days: next }));
    return next;
  } catch {
    return null;
  }
}

/**
 * True on the first days this month the page is opened, as many as the
 * settings say, and on any of those days however often it is opened again.
 *
 * @param iso - The gardener's today, "YYYY-MM-DD".
 * @returns Whether to say the wettest or driest month today.
 */
export function useRainMonth(iso: string): boolean {
  const [days] = useState(() => openingDays(iso));
  const opens = useGrowSettings()['today.RAIN_MONTH_OPENS'];
  // Not said before the app has heard the setting, nor when it is 0 (never).
  if (opens === undefined || opens <= 0) return false;
  // Storage refused: saying it is the better failure.
  if (days === null) return true;
  const index = days.indexOf(iso);
  return index >= 0 && index < opens;
}
